import { createContext, useContext, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { NotificationService, NOTIFICATION_TYPE_SUCCESS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { fetchAutomationRules, addRule, updateRule } from '../../pages/settings/api/automation-rules';
import { generateNewAutomationRule } from '../../pages/settings/automation-rules/services';
import { LogService } from '../../services/log-service';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { SYSTEM_RULE_CONFIGS, SYSTEM_RULE_NAMES } from './constants';
import { messages } from './messages';

const SystemAutomationContext = createContext();

export const useSystemAutomation = () => {
  const context = useContext(SystemAutomationContext);
  if (!context) {
    throw new Error('useSystemAutomation must be used within SystemAutomationContextProvider');
  }
  return context;
};

/**
 * Finds a system rule by name in the rules array
 * @param {Array} rules - Array of automation rules
 * @param {string} ruleName - Name of the rule to find
 * @returns {object|null} The found rule or null
 */
const findSystemRuleByName = (rules, ruleName) => {
  return rules.find((rule) => rule.name === ruleName) || null;
};

/**
 * Finds the packing-recommendation system rule, preferring an exact name match and falling back
 * to matching on the rule's action type. The fallback catches a rule the seller already built for
 * the same action under a different name, so toggling the rule adopts and controls it instead of
 * creating a duplicate that would run the same action twice on every order.
 * @param {Array} rules - Array of automation rules
 * @returns {object|null} The found rule or null
 */
const findPackingRecommendationRule = (rules) => {
  const exactNameMatch = rules.find((rule) => rule.name === SYSTEM_RULE_NAMES.PACKING_RECOMMENDATION);
  if (exactNameMatch) {
    return exactNameMatch;
  }

  const config = SYSTEM_RULE_CONFIGS[SYSTEM_RULE_NAMES.PACKING_RECOMMENDATION];
  return rules.find((rule) => rule.orderRuleActions?.some((action) => action.actionTypeId === config.actionTypeId)) || null;
};

/**
 * Creates automation rules hash keyed by serviceCollectionGuid for backward compatibility
 * @param {Array} rules - Array of automation rules
 * @param {Array} serviceCollections - Array of service collections
 * @returns {object} Hash of rules keyed by serviceCollectionGuid
 */
const createServiceCollectionAutomationRulesHash = (rules, serviceCollections = []) => {
  const hash = {};

  serviceCollections.forEach((serviceCollection) => {
    const rulesForCollection = rules.filter((rule) => {
      return rule.orderRuleActions?.some((action) => {
        if (action?.parameter?.toString().length > 0) {
          let parameterValue = action.parameter;
          if (typeof parameterValue === 'string' && parameterValue.startsWith('{')) {
            try {
              parameterValue = JSON.parse(parameterValue);
            } catch (e) {
              LogService.logError(new Error('Failed to parse parameter as JSON', { cause: e }));
            }
          }

          // If it's an object, we can't split it, so return false for this legacy logic
          if (typeof parameterValue === 'object') {
            return false;
          }

          const splitValue = parameterValue.split(',');
          if (!splitValue || !splitValue.length) return false;
          const serviceGuid = splitValue[0];
          return serviceGuid === serviceCollection.serviceCollectionGuid;
        }
        return false;
      });
    });

    if (rulesForCollection.length > 0) {
      hash[serviceCollection.serviceCollectionGuid] = rulesForCollection;
    }
  });

  return hash;
};

const informationalTimeoutInMilliseconds = 3000;
const errorTimeoutInMilliseconds = 5000;

export const SystemAutomationContextProvider = ({ children }) => {
  const intl = useIntl();
  const [automationRules, setAutomationRules] = useAsyncSafeState(emptyArray);
  const [automationRulesHash, setAutomationRulesHash] = useAsyncSafeState(emptyObject);
  const [isLoading, setIsLoading] = useAsyncSafeState(true);
  const [error, setError] = useAsyncSafeState(null);

  const refreshAutomationRules = useCallback(
    async (serviceCollections = []) => {
      try {
        setIsLoading(true);
        setError(null);
        const rules = await fetchAutomationRules();
        setAutomationRules(rules || []);
        // Create hash for backward compatibility with RateShopperContext
        const hash = createServiceCollectionAutomationRulesHash(rules || [], serviceCollections);
        setAutomationRulesHash(hash);

        return rules || [];
      } catch (err) {
        const isIgnorable = err?.response || err?.code === 'ERR_CANCELED';
        if (!isIgnorable) {
          LogService.logError(new Error('Failed to load automation rules', { cause: err }));
        }
        const errorMessage = intl.formatMessage(messages.loadAutomationRulesError);
        setError(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [intl, setAutomationRules, setAutomationRulesHash, setError, setIsLoading],
  );

  const getSystemRule = useCallback(
    (ruleName) => {
      return findSystemRuleByName(automationRules, ruleName);
    },
    [automationRules],
  );

  const getPackingRecommendationRule = useCallback(() => {
    return findPackingRecommendationRule(automationRules);
  }, [automationRules]);

  const isSystemRuleActive = useCallback(
    (ruleName) => {
      const rule = getSystemRule(ruleName);
      return rule ? rule.active : false;
    },
    [getSystemRule],
  );

  const createSystemRule = useCallback(
    async (ruleName, ruleConfig = {}, options = {}) => {
      const { silent = false } = options;
      try {
        setError(null);
        const config = SYSTEM_RULE_CONFIGS[ruleName];
        if (!config) {
          throw new Error(`No configuration found for system rule: ${ruleName}`);
        }
        const parameter = ruleConfig.parameter || config.defaultParameter || null;
        const orderRuleActions = [
          {
            actionTypeId: config.actionTypeId,
            parameter: typeof parameter === 'object' && parameter !== null ? JSON.stringify(parameter) : parameter,
          },
        ];
        const newRule = generateNewAutomationRule({
          name: ruleName,
          filterId: config.filterId,
          orderRuleActions,
          ...ruleConfig,
        });
        await addRule(newRule);
        await refreshAutomationRules();

        if (!silent) {
          NotificationService.showNotification({
            title: intl.formatMessage(messages.createSuccessTitle),
            notificationType: NOTIFICATION_TYPE_SUCCESS,
            content: intl.formatMessage(messages.createSuccessContent),
            duration: informationalTimeoutInMilliseconds,
          });
        }

        return newRule;
      } catch (err) {
        LogService.logError(new Error('Failed to create system rule', { cause: err }));
        const errorMessage = intl.formatMessage(messages.createFailedError);
        setError(errorMessage);

        NotificationService.showNotification({
          title: intl.formatMessage(messages.createFailedTitle),
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: errorMessage,
          duration: errorTimeoutInMilliseconds,
        });

        throw err;
      }
    },
    [intl, refreshAutomationRules, setError],
  );

  const performRuleUpdate = useCallback(
    async (existingRule, updates, options = {}) => {
      const { silent = false } = options;
      try {
        setError(null);
        const updatedRule = {
          ...existingRule,
          ...updates,
        };
        await updateRule(updatedRule);
        await refreshAutomationRules();

        if (!silent) {
          NotificationService.showNotification({
            title: intl.formatMessage(messages.updateSuccessTitle),
            notificationType: NOTIFICATION_TYPE_SUCCESS,
            content: intl.formatMessage(messages.updateSuccessContent),
            duration: informationalTimeoutInMilliseconds,
          });
        }

        return updatedRule;
      } catch (err) {
        LogService.logError(new Error('Failed to update system rule', { cause: err }));
        const errorMessage = intl.formatMessage(messages.updateFailedError);
        setError(errorMessage);

        NotificationService.showNotification({
          title: intl.formatMessage(messages.updateFailedTitle),
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: errorMessage,
          duration: errorTimeoutInMilliseconds,
        });

        throw err;
      }
    },
    [intl, refreshAutomationRules, setError],
  );

  const updateSystemRule = useCallback(
    async (ruleName, updates, options = {}) => {
      const existingRule = getSystemRule(ruleName);
      if (!existingRule) {
        throw new Error(`System rule not found: ${ruleName}`);
      }
      return performRuleUpdate(existingRule, updates, options);
    },
    [getSystemRule, performRuleUpdate],
  );

  const createOrUpdateSystemRule = useCallback(
    async (ruleName, ruleConfig = {}, options = {}) => {
      const existingRule = getSystemRule(ruleName);

      if (existingRule) {
        return updateSystemRule(ruleName, ruleConfig, options);
      }
      return createSystemRule(ruleName, ruleConfig, options);
    },
    [getSystemRule, updateSystemRule, createSystemRule],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, [setError]);

  const isIncludeReturnLabelRuleActive = useCallback(() => {
    return isSystemRuleActive(SYSTEM_RULE_NAMES.INCLUDE_RETURN_LABEL);
  }, [isSystemRuleActive]);

  const toggleIncludeReturnLabelRule = useCallback(
    async (isActive) => {
      const ruleName = SYSTEM_RULE_NAMES.INCLUDE_RETURN_LABEL;
      const existingRule = getSystemRule(ruleName);

      if (existingRule) {
        // Update existing rule's active state and ensure parameter is preserved
        const config = SYSTEM_RULE_CONFIGS[ruleName];
        const updatedActions = existingRule.orderRuleActions.map((action) => {
          if (action.actionTypeId === config.actionTypeId) {
            const parameter = action.parameter || config.defaultParameter || null;
            return {
              ...action,
              parameter: typeof parameter === 'object' && parameter !== null ? JSON.stringify(parameter) : parameter,
            };
          }
          return action;
        });

        return createOrUpdateSystemRule(ruleName, {
          active: isActive,
          orderRuleActions: updatedActions,
        });
      }
      if (isActive) {
        return createOrUpdateSystemRule(ruleName, { active: true });
      }
      return null;
    },
    [getSystemRule, createOrUpdateSystemRule],
  );

  const isPackingRecommendationRuleActive = useCallback(() => {
    const rule = getPackingRecommendationRule();
    return rule ? rule.active : false;
  }, [getPackingRecommendationRule]);

  const togglePackingRecommendationRule = useCallback(
    async (isActive, options = {}) => {
      const existingRule = getPackingRecommendationRule();

      if (existingRule) {
        return performRuleUpdate(existingRule, { active: isActive }, options);
      }
      if (isActive) {
        return createOrUpdateSystemRule(SYSTEM_RULE_NAMES.PACKING_RECOMMENDATION, { active: true }, options);
      }
      return null;
    },
    [getPackingRecommendationRule, performRuleUpdate, createOrUpdateSystemRule],
  );

  useEffect(() => {
    // don't let the re-throw in the catch block in the refreshAutomationRules cb
    // to affect things like app load
    refreshAutomationRules().catch(() => {
      // ignore errors here, they are logged in the callback and then re-thrown
    });
  }, [refreshAutomationRules]);

  const contextValue = {
    automationRules,
    automationRulesHash,
    isLoading,
    error,
    refreshAutomationRules,
    getSystemRule,
    getPackingRecommendationRule,
    isSystemRuleActive,
    createSystemRule,
    updateSystemRule,
    createOrUpdateSystemRule,
    clearError,
    isIncludeReturnLabelRuleActive,
    toggleIncludeReturnLabelRule,
    isPackingRecommendationRuleActive,
    togglePackingRecommendationRule,
  };

  return <SystemAutomationContext.Provider value={contextValue}>{children}</SystemAutomationContext.Provider>;
};

SystemAutomationContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
